import React from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Globe, Lock, MoreVertical, Pencil, Copy, Trash2, FolderOpen, ClipboardList, User } from 'lucide-react';
import { TEMPLATE_TYPES, countTemplatesByType } from '@/lib/templateFolders';

/** One Template Folder tile in the library grid (section 2/4). */
export function TemplateFolderCard({ folder, isPublic, onOpen, onEdit, onDuplicate, onDelete, onCopyToMyLibrary }) {
  const counts = countTemplatesByType(folder);
  const totalTemplates = Object.values(counts).reduce((a, b) => a + b, 0);
  const hasBlueprint = (folder.segments?.length || 0) > 0 || (folder.outputs?.length || 0) > 0;

  return (
    <Card className="glass border-white/10 p-4 flex flex-col gap-3 hover:border-[#3A7BFF]/30 transition-colors" data-testid={`template-folder-card-${folder.id}`}>
      <div className="flex items-start justify-between gap-2">
        <button onClick={() => onOpen(folder)} className="text-left flex-1 min-w-0" data-testid={`template-folder-open-${folder.id}`}>
          <p className="text-sm font-semibold text-white truncate">{folder.name}</p>
          {folder.description && <p className="text-xs text-[#8A93A6] line-clamp-2 mt-0.5">{folder.description}</p>}
        </button>
        {!isPublic && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-[#8A93A6] hover:text-white shrink-0" data-testid={`template-folder-menu-${folder.id}`}><MoreVertical className="h-4 w-4" /></Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-[#1C1F26] border-white/10">
              <DropdownMenuItem onClick={() => onEdit(folder)} data-testid={`template-folder-edit-${folder.id}`}><Pencil className="h-3.5 w-3.5 mr-2" />Edit</DropdownMenuItem>
              <DropdownMenuItem onClick={() => onDuplicate(folder)} data-testid={`template-folder-duplicate-${folder.id}`}><Copy className="h-3.5 w-3.5 mr-2" />Duplicate</DropdownMenuItem>
              <DropdownMenuItem onClick={() => onDelete(folder)} className="text-red-400 focus:text-red-400" data-testid={`template-folder-delete-${folder.id}`}><Trash2 className="h-3.5 w-3.5 mr-2" />Delete</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      <div className="flex items-center gap-1.5 flex-wrap">
        <Badge variant="outline" className={`text-[9px] gap-1 ${folder.visibility === 'public' ? 'border-green-500/30 text-green-300 bg-green-500/10' : 'border-white/10 text-[#8A93A6]'}`}>
          {folder.visibility === 'public' ? <Globe className="h-2.5 w-2.5" /> : <Lock className="h-2.5 w-2.5" />}{folder.visibility === 'public' ? 'Public' : 'Private'}
        </Badge>
        {hasBlueprint && <Badge variant="outline" className="text-[9px] gap-1 border-[#3A7BFF]/30 text-[#3A7BFF] bg-[#3A7BFF]/5"><ClipboardList className="h-2.5 w-2.5" />Blueprint</Badge>}
        {isPublic && folder.ownerCreatorName && <Badge variant="outline" className="text-[9px] gap-1 border-white/10 text-[#8A93A6]"><User className="h-2.5 w-2.5" />{folder.ownerCreatorName}</Badge>}
      </div>

      <div className="flex flex-wrap gap-1">
        {TEMPLATE_TYPES.filter((t) => counts[t.key] > 0).map((t) => (
          <Badge key={t.key} variant="secondary" className="bg-white/5 text-[#C0C7D4] border-none text-[9px]" data-testid={`template-folder-count-${folder.id}-${t.key}`}>{t.label.replace(' Template', '').replace(' Preset', '')} · {counts[t.key]}</Badge>
        ))}
        {totalTemplates === 0 && <span className="text-[10px] text-[#555E6E]">No templates yet</span>}
      </div>

      <div className="flex gap-2 mt-auto pt-1">
        {isPublic ? (
          <Button size="sm" variant="outline" className="flex-1 border-white/10 text-xs" onClick={() => onCopyToMyLibrary(folder)} data-testid={`template-folder-copy-${folder.id}`}><Copy className="h-3.5 w-3.5 mr-1.5" />Copy to My Library</Button>
        ) : (
          <Button size="sm" variant="outline" className="flex-1 border-white/10 text-xs" onClick={() => onOpen(folder)} data-testid={`template-folder-manage-${folder.id}`}><FolderOpen className="h-3.5 w-3.5 mr-1.5" />Open</Button>
        )}
      </div>
    </Card>
  );
}
